import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { authService } from '../../services/auth/auth.service';
import { useAuthStore } from '../../store/auth.store';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isForced?: boolean;
}

export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  isOpen,
  onClose,
  isForced = false,
}) => {
  const [contrasenaActual, setContrasenaActual] = useState('');
  const [nuevaContrasena, setNuevaContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [mostrarNueva, setMostrarNueva] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { loadUser } = useAuthStore();

  const coinciden = nuevaContrasena && confirmarContrasena && nuevaContrasena === confirmarContrasena;
  const noCoinciden = nuevaContrasena && confirmarContrasena && nuevaContrasena !== confirmarContrasena;

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (nuevaContrasena.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (nuevaContrasena !== confirmarContrasena) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setIsLoading(true);
    try {
      await authService.cambiarContrasena(contrasenaActual, nuevaContrasena);
      setSuccess(true);
      // Recargar datos del usuario para actualizar requiereCambioContrasena
      await loadUser();
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setContrasenaActual('');
        setNuevaContrasena('');
        setConfirmarContrasena('');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Cambiar contraseña</h2>
              {isForced && (
                <p className="text-purple-100 text-xs">Es obligatorio para continuar</p>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {isForced && (
            <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Tu cuenta requiere un cambio de contraseña por seguridad. Debes cambiarla antes de continuar usando el sistema.
              </p>
            </div>
          )}

          {success ? (
            <div className="flex flex-col items-center py-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">¡Contraseña actualizada!</h3>
              <p className="text-sm text-gray-500 mt-1">Tu contraseña se ha cambiado exitosamente.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isForced && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña actual
                  </label>
                  <input
                    type="password"
                    value={contrasenaActual}
                    onChange={(e) => setContrasenaActual(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none text-sm"
                    placeholder="••••••"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={mostrarNueva ? 'text' : 'password'}
                    value={nuevaContrasena}
                    onChange={(e) => setNuevaContrasena(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none text-sm pr-10"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarNueva(!mostrarNueva)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {mostrarNueva ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={mostrarConfirmar ? 'text' : 'password'}
                    value={confirmarContrasena}
                    onChange={(e) => setConfirmarContrasena(e.target.value)}
                    className={`w-full px-3 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm pr-16 transition-colors ${
                      coinciden
                        ? 'border-green-400 focus:ring-green-500/30 focus:border-green-500'
                        : noCoinciden
                        ? 'border-red-400 focus:ring-red-500/30 focus:border-red-500'
                        : 'border-gray-200 focus:ring-purple-500/30 focus:border-purple-500'
                    }`}
                    placeholder="Repite la contraseña"
                  />
                  {/* Indicador de coincidencia */}
                  <div className="absolute right-8 top-1/2 -translate-y-1/2">
                    {coinciden && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {noCoinciden && (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {mostrarConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Mensaje de estado */}
                {coinciden && (
                  <p className="text-xs text-green-600 mt-1 flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Las contraseñas coinciden
                  </p>
                )}
                {noCoinciden && (
                  <p className="text-xs text-red-600 mt-1 flex items-center">
                    <XCircle className="w-3 h-3 mr-1" />
                    Las contraseñas no coinciden
                  </p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                {!isForced && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isLoading || !nuevaContrasena || !confirmarContrasena || (!isForced && !contrasenaActual)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                  ) : (
                    'Cambiar contraseña'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
